import { TrialPlan } from 'hydra-data-types';
import React, { FunctionComponent, ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { appOverride } from '../../util/AppUtils';
import { LayoutScreenConfig, ScreenType, SharedScreenName } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { PricingSelector } from '../../store/pricing/PricingActions';
import { NavigationProps } from '../flows/FlowNavigation';
import { PresentationLayout } from '../flows/layouts/PresentationLayout';
import { ScreenWrapper } from '../styles/Containers';
import { ContentLoading } from '../styles/ContentLoading';
import { Spacer } from '../styles/Dividers';
import { BodyXS } from '../styles/Typography';
import { TrialOptionButtons } from './TrialOptionButtons';

type WorksBestLayoutConfig = {
  title: string;
  body: string[];
  description: string[];
  primaryButtonTitle: string;
  textAnimationEnabled?: boolean;
};

export type WorksBestScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.WorksBest,
  WorksBestLayoutConfig,
  ScreenNames
>;

type WorksBestScreenProps<ScreenNames extends string> = NavigationProps<
  WorksBestScreenConfig<ScreenNames>,
  ScreenNames
>;

export const WorksBestScreen = <ScreenNames extends string>(
  props: WorksBestScreenProps<ScreenNames>
): ReactElement => {
  const { navigation, layout } = props;

  const { pricing, userData, trialPeriodDays } = useAppStore((state) => ({
    pricing: state.pricing,
    userData: AppSelector.userData(state),
    trialPeriodDays: state.purchase.trialPeriodDays,
  }));

  // Skip this screen if the user has answered yes to being referred by a doctor
  const userDoctorAnsweredYes = userData.doctor?.[0] === 'Yes';

  useEffect(
    function skipScreenIfReferredByDoctor() {
      if (userDoctorAnsweredYes) {
        navigation.goTo(SharedScreenName.Payment as ScreenNames);
      }
    },
    [userDoctorAnsweredYes]
  );

  return (
    <ScreenWrapper>
      {pricing.loading ? (
        <ContentLoading />
      ) : (
        <PresentationLayout
          title={layout.title}
          content={[
            ...layout.body.map((content) => ({
              type: 'text' as const,
              content,
            })),
            { type: 'custom', content: <Spacer space={2} /> },
            {
              type: 'custom',
              content: (
                <TrialDescription
                  description={layout.description}
                  price={PricingSelector.priceForTrialPlan(
                    pricing,
                    TrialPlan.SixteenDollarTrial
                  ).toString()}
                  period={trialPeriodDays}
                />
              ),
            },
            {
              type: 'custom',
              content: <TrialOptionButtons />,
            },
          ]}
          primaryButtonTitle={layout.primaryButtonTitle}
          onPressPrimaryButton={() => navigation.next(props)}
          textAnimationEnabled={layout.textAnimationEnabled}
        />
      )}
    </ScreenWrapper>
  );
};

type Props = {
  description: string[];
  price: string;
  period: number;
};

const TrialDescription: FunctionComponent<Props> = (props) => (
  <TrialDescriptionContainer>
    <TrialDescriptionTitle>
      {props.description[0]
        .replace('{{trialPrice}}', props.price)
        .replace('{{trialPeriod}}', props.period.toString())}
    </TrialDescriptionTitle>
    <Paragraph>{props.description[1]}</Paragraph>
  </TrialDescriptionContainer>
);

const TrialDescriptionContainer = styled.div`
  background-color: ${appOverride({
    nerva: 'rgba(0, 0, 0, 0.2)',
    default: Color.subscription.surface,
  })};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing._16px};
  text-align: left;
  border: ${appOverride({ nerva: 'none', default: '1px solid transparent' })};
`;

const TrialDescriptionTitle = styled(BodyXS)`
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${Color.content.default};
`;

const Paragraph = styled(BodyXS)`
  margin: 0px;
`;
